import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { trackLead } from '@/lib/metaPixel';
import { linkSignup } from '@/lib/growthTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Google OAuth callback handler.
 *
 * Google redirects the user to `${origin}/auth/google?code=...&state=...`.
 * We pull the code, verify the state we set before redirecting, exchange
 * the code with our backend (which talks to Google directly), receive a
 * session token, then route the user into the app.
 */
export default function GoogleCallbackPage() {
  const navigate = useNavigate();
  const { setUser, refreshUser, claimPendingGuestEvent } = useAuth();
  const [error, setError] = useState(null);
  // Guard against React 18 StrictMode double-invocation in dev — a Google
  // authorization code is single-use; exchanging it twice yields 400.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error');
      const expectedState = sessionStorage.getItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_state');

      if (oauthError) {
        setError(oauthError === 'access_denied'
          ? 'Sign-in was cancelled.'
          : `Google rejected the sign-in: ${oauthError}`);
        return;
      }
      if (!code) {
        setError('Missing authorization code from Google.');
        return;
      }
      if (!state || state !== expectedState) {
        setError('Sign-in security check failed. Please try again.');
        return;
      }

      const redirectUri = `${window.location.origin}/auth/google`;
      try {
        const res = await fetch(`${API_URL}/api/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, redirect_uri: redirectUri }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!data.session_token || !data.user) {
          throw new Error('Invalid response from server');
        }
        localStorage.setItem('session_token', data.session_token);
        if (typeof setUser === 'function') setUser(data.user);
        // Meta Pixel: only fire Lead if the backend tells us this Google
        // flow just created a brand-new account. Returning sign-ins skip
        // the event so we don't inflate the funnel.
        if (data.is_new_user) {
          trackLead('google');
          linkSignup(data.session_token);
        }
        // If they came from a guest-created schedule, attach it now so it
        // shows in /my-schedules alongside future ones.
        let claimedEventId = null;
        if (typeof claimPendingGuestEvent === 'function') {
          try { claimedEventId = await claimPendingGuestEvent(data.session_token); } catch { /* non-fatal */ }
        }
        if (typeof refreshUser === 'function') {
          try { await refreshUser(); } catch { /* non-fatal */ }
        }
        toast.success(`Welcome, ${data.user.name || data.user.email}!`);
        const nextPath = sessionStorage.getItem('sg_post_login_redirect');
        sessionStorage.removeItem('sg_post_login_redirect');
        if (claimedEventId) {
          toast.success('Your guest schedule was added to your account.');
          navigate(`/schedule/${claimedEventId}?welcome_tour=1`, { replace: true });
        } else {
          navigate(nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//') ? nextPath : '/my-schedules', { replace: true });
        }
      } catch (e) {
        setError(e.message || 'Google sign-in failed.');
      }
    })();
  }, [navigate, setUser, refreshUser, claimPendingGuestEvent]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-card p-8 text-center" data-testid="google-callback-page">
        {error ? (
          <>
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-800 mb-2">Sign-in failed</h2>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <button
              type="button"
              className="text-[#00247D] font-medium hover:underline text-sm"
              onClick={() => navigate('/login', { replace: true })}
              data-testid="google-callback-retry-btn"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-[#00247D] animate-spin mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-800 mb-1">Signing you in…</h2>
            <p className="text-sm text-slate-500">Verifying your Google account.</p>
          </>
        )}
      </div>
    </div>
  );
}
